import { useModelActions, useModelState } from '@rexlabs/model-generator';
import React, { FC, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import sessionModel from 'src/data/models/custom/session';
import LoadingLayout from 'src/view/layouts/loading';

/**
 * Parent of every react-router account route (`/:accountIndex/...`: the
 * campaign wizards and the post wizard).
 *
 * The whereabouts-driven layouts are only rendered by SpokeApp once the
 * session model is ready and the URL's account index has been reconciled
 * with the store. These routes bypass that, so on a hard load they used to
 * render immediately: account-scoped links (NavBar's logo, ErrorLayout's
 * home link) derive `:accountIndex` from `session.accounts`, which is still
 * empty, and the whereabouts Link throws on the resulting `-1`, leaving a
 * blank page. Mirror the SpokeApp gate here instead.
 */
export const SelectedAccountLayout: FC = () => {
  const { accountIndex = '' } = useParams();
  const location = useLocation();
  const session = useModelState(sessionModel);
  const { switchAccount } = useModelActions(sessionModel);

  // The URL's account index is the source of truth (see ensureAccountIndex in
  // src/utils/router.js): a hard load of another account's wizard URL must
  // switch to that account rather than run against whichever one was last
  // selected.
  const urlIndex = /^\d+$/.test(accountIndex) ? parseInt(accountIndex, 10) : -1;
  const urlAccount = urlIndex >= 0 ? session.accounts?.[urlIndex] : undefined;
  const currentIndex = (session.accounts ?? []).findIndex(
    (account: { id: string }) => account.id === session.currentAccountId
  );
  const needsSwitch =
    session.ready &&
    session.status !== 'switching' &&
    !!urlAccount &&
    urlAccount.id !== session.currentAccountId;

  useEffect(() => {
    if (needsSwitch && urlAccount) {
      switchAccount({ id: urlAccount.id });
    }
  }, [needsSwitch, urlAccount, switchAccount]);

  if (!session.ready || session.status === 'switching' || needsSwitch) {
    return <LoadingLayout />;
  }

  if (!session.currentAccountId) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/select-agency?redirect=${redirect}`} replace />;
  }

  // A stale or bookmarked URL can name an index that no longer exists (the
  // user's account access changed). Correct the URL to the selected account
  // rather than silently running the route against it under the wrong index,
  // matching ensureAccountIndex for the whereabouts routes.
  if (!urlAccount && currentIndex >= 0) {
    const corrected = location.pathname.replace(/^\/[^/]+/, `/${currentIndex}`);
    return <Navigate to={corrected + location.search} replace />;
  }

  return (
    <Suspense>
      <Outlet />
    </Suspense>
  );
};
